/**
 * One explainer per treatment step, written once and read by every surface that
 * explains the step.
 *
 * The Care treatment hub used to assemble its step panels from three catalogue
 * sources at once (phase copy, phase extensions and per-step overrides), merged
 * field by field, while Home wrote its own slides for the same steps. The
 * versions drifted: three fasting rules for one retrieval morning, the egg
 * retrieval warning signs on a sperm sample, IVF injectables on an IUI cycle.
 * Here each step has one explainer, chosen by one resolver, in one catalogue
 * namespace (`step_explainers`) that belongs to neither surface.
 *
 * Every explainer has the same five sections, in the same order, and every one
 * says when to call the clinic. Variants (cycle type, frozen eggs or embryos, a
 * trigger shot, a partner reading) are items that apply only in that case, never
 * a second copy of the explainer. Copy shared by two explainers is written in
 * one and pointed to from the other.
 *
 * These are content types, not API data: the cycle type comes from the API, the
 * step ids from the treatment flows, and the rest is the shape of the copy.
 *
 * ------------------------------------------------------------------------
 * SOURCES
 * ------------------------------------------------------------------------
 * The medical statements follow ASRM practice committee documents, ESHRE
 * guidelines and WHO, then peer-reviewed papers; RCOG, NICE, HFEA and FDA
 * patient guidance only where they say the same thing more plainly. The spec
 * entries below name their source by the short label in brackets. Change a
 * statement only for a newer source, and cite it. Where clinics differ, the
 * copy says the clinic decides rather than picking one practice.
 *
 * ASRM
 *   - [ASRM OHSS 2024] Practice Committee, Prevention of moderate and severe
 *     ovarian hyperstimulation syndrome: a guideline (Fertil Steril
 *     2024;121:230), PMID 38099867. Signs by severity (bloating, nausea or
 *     vomiting, breathlessness, much less urine, rapid weight gain, fainting,
 *     thrombosis); early OHSS 4 to 7 days after the hCG trigger, late OHSS 9 or
 *     more days after it, driven by a pregnancy.
 *     https://www.asrm.org/practice-guidance/practice-committee-documents/prevention-of-moderate-and-severe-ovarian-hyperstimulation-syndrome-a-guideline-2023/
 *   - [ASRM OHSS facts 2023] ReproductiveFacts patient fact sheet, OHSS: call
 *     for trouble breathing, continued nausea or vomiting, a swelling belly,
 *     less urine, weight gain, sudden belly pain, a swollen or red leg; OHSS
 *     also follows ovulation induction.
 *     https://www.reproductivefacts.org/news-and-publications/fact-sheets-and-infographics/ovarian-hyperstimulation-syndrome-ohss/
 *   - [ASRM gonadotropin OI 2020] Use of exogenous gonadotropins for ovulation
 *     induction in anovulatory women (Fertil Steril 2020), PMID 32033726: OHSS
 *     can occur after ovulation induction.
 *   - [ASRM ovarian reserve 2020] Testing and interpreting measures of ovarian
 *     reserve (Fertil Steril 2020;114:1151), PMID 33280722: FSH and estradiol
 *     on cycle day 2 to 4, antral follicles counted by transvaginal scan.
 *   - [ASRM unexplained 2020] Evidence-based treatments for couples with
 *     unexplained infertility: a guideline (Fertil Steril 2020), PMID 32106976:
 *     clomiphene or letrozole with IUI; IUI 0 to 36 hours after an hCG trigger;
 *     no evidence that scan timing beats urine LH timing.
 *   - [ASRM transfer 2017] Performing the embryo transfer: a guideline (Fertil
 *     Steril 2017;107:882), PMID 28366416: bed rest is not recommended;
 *     ultrasound guidance is.
 *   - [ASRM embryos 2021] Guidance on the limits to the number of embryos to
 *     transfer (Fertil Steril 2021;116:651), PMID 34330423: one embryo for most.
 *   - [ASRM PGT-A 2024] The use of preimplantation genetic testing for
 *     aneuploidy (Fertil Steril 2024;122:421), PMID 38762806: not shown to help
 *     every patient; "no result" is a possible outcome. [ASRM mosaic 2023]
 *     Clinical management of mosaic results (Fertil Steril 2023;120:973), PMID
 *     37678731: euploid, aneuploid and mosaic reports.
 *   - [ASRM oocyte cryo 2013] Mature oocyte cryopreservation: a guideline
 *     (Fertil Steril 2013;99:37), PMID 23083924: 90 to 97% of eggs survive
 *     warming; ICSI is generally used.
 *   - [ASRM natural fertility 2022] Optimizing natural fertility (Fertil Steril
 *     2022), PMID 34815068: saliva and some lubricants harm sperm; recreational
 *     drugs and heavy alcohol lower sperm quality.
 *   - [AUA/ASRM male 2020] Diagnosis and treatment of infertility in men (Fertil
 *     Steril 2021), PMID 33309061, statement 45 as amended in 2024: supplements
 *     are of questionable clinical use.
 *     https://www.auanet.org/guidelines-and-quality/guidelines/male-infertility
 *   - [ASRM viral 2023] Reducing viral transmission with autologous gametes
 *     (Fertil Steril 2023), PMID 37656091: viral screening before ART.
 * ESHRE
 *   - [ESHRE OS 2025] Ovarian stimulation for IVF/ICSI, 2019 (Hum Reprod Open
 *     2020;hoaa009, PMID 32395637) and its 2025 update (Hum Reprod
 *     2026;41:498, PMID 41732035): FSH alone or with LH, and hMG, are equally
 *     recommended; estradiol added to scans is probably not; progesterone
 *     luteal support until at least the pregnancy test; the pill before an
 *     antagonist protocol is not recommended, estrogen or progesterone may be
 *     used for scheduling.
 *   - [ESHRE OPU 2019] D'Angelo et al., Recommendations for good practice in
 *     ultrasound: oocyte pick up (Hum Reprod Open 2019;hoz025), PMID 31844683:
 *     retrieval about 36 hours (34 to 38) after the trigger; bleeding and
 *     infection after it; signs of infection are checked before it.
 *   - [ESHRE add-ons 2023] Good practice recommendations on add-ons (Hum Reprod
 *     2023;38:2062), PMID 37747409: antioxidants are not recommended; PGT-A is
 *     not for routine use.
 *   - [ESHRE unexplained 2023] Unexplained infertility (Hum Reprod 2023), PMID
 *     37599566: IUI with stimulation, low-dose gonadotropins with monitoring to
 *     avoid multiples and OHSS.
 *   - [PCOS 2023] Teede et al., International evidence-based guideline for PCOS
 *     (Hum Reprod 2023), PMID 37580037: letrozole first, then gonadotropins with
 *     ultrasound monitoring.
 *   - [ESHRE psychosocial 2015] Routine psychosocial care in infertility (Hum
 *     Reprod 2015;30:2476), PMID 26345684: distress peaks in the wait; offer
 *     support.
 *   - [Vienna 2017] ESHRE/Alpha lab performance indicators (Hum Reprod Open
 *     2017;hox011), PMID 31486806: normal fertilization of 60 to 65% of mature
 *     eggs is competent, 75 to 80% is the benchmark; 90% or more of
 *     blastocysts survive warming.
 *   - [Istanbul 2025] ESHRE/Alpha embryo assessment consensus, 2011 (PMID
 *     21502182) and 2025 update (Hum Reprod 2025;40:989, PMID 40288770): 8
 *     cells expected on day 3; blastocysts on day 5, 6 or 7.
 * WHO
 *   - [WHO semen 2021] Laboratory manual for the examination and processing of
 *     human semen, 6th edition: 2 to 7 days of abstinence; the first portion is
 *     the richest in sperm; no ordinary condoms or lubricants; a home sample
 *     kept at 20 to 37 °C and delivered within 30, at most 50, minutes.
 *     https://www.who.int/publications/i/item/9789240030787
 *   - [USPSTF folic acid 2023] JAMA 2023, PMID 37526713 (with WHO antenatal
 *     care 2016): folic acid for anyone planning a pregnancy.
 * Papers and other guidance
 *   - [Boivin 2011] BMJ 2011;342:d223, PMID 21345903: distress does not lower
 *     the chance of pregnancy with treatment.
 *   - [Kasius 2014] Hum Reprod Update 2014;20:530, PMID 24664156: no lining
 *     thickness cut-off justifies cancelling. [Mumusoglu 2021] Front
 *     Endocrinol 2021;12:688237, PMID 34305815: estrogen for about 12 to 14
 *     days; the transfer day follows the progesterone start; with no corpus
 *     luteum, progesterone continues to 10 to 12 weeks.
 *   - [hCG clearance] Damewood et al., Fertil Steril 1989;52:398, PMID 2776893:
 *     trigger hCG detectable up to 14 days; EU product information for hCG
 *     triggers: false positive tests for up to 10 days.
 *   - [Hughes 2022] J Assist Reprod Genet 2022;39:2355, PMID 36074224, and
 *     Kumbak 2006, PMID 17007661: blood test 8 to 12 days after a transfer,
 *     repeated 48 hours later.
 *   - [Ectopic] NICE NG126 (2019, updated), recommendation 1.4.3: pelvic pain,
 *     bleeding, dizziness or fainting, shoulder tip pain.
 *     https://www.nice.org.uk/guidance/ng126 ; ACOG Practice Bulletin 193
 *     (2018), PMID 29470343; Perkins et al. 2015, PMID 25560107: 1.6 to 2% of
 *     IVF pregnancies; Fernandez et al. 1991, PMID 1810289: raised after
 *     ovulation induction.
 *   - [Torsion] Gorkemli et al. 2002, PMID 12410364; Mashiach et al. 1990,
 *     PMID 2295348: rare, sudden severe pain, more often with OHSS.
 *   - [Retrieval bleeding] Levi-Setti et al. 2018, PMID 29871795; Chen et al.
 *     2025, PMID 40584299: dizziness or fainting in most bleeds after
 *     retrieval.
 *   - [RCOG OHSS 2016] Green-top guideline 5 and patient leaflet: a swollen,
 *     tender leg or chest pain with breathlessness needs care.
 *   - [Sergerie 2007] Fertil Steril 2007, PMID 17434502: sperm counts fall for
 *     about two months after a fever.
 *   - [MOXI 2020] Steiner et al., Fertil Steril 2020, PMID 32111479:
 *     antioxidants did not improve semen or live births.
 *   - [Heat and alcohol] Garolla et al. 2013, PMID 23411620 (sauna); Ricci et
 *     al. 2017, PMID 28029592 (heavy drinking).
 *   - [IUI timing 2025] Cochrane, Adesina et al. 2025, PMID 40985294: no
 *     method of timing IUI shown better than another. [Green 2017] Fertil
 *     Steril 2017;107:924, PMID 28238492: luteal support helps after
 *     gonadotropins, not clomiphene.
 *   - [FDA pregnancy tests] Testing too early can miss a pregnancy.
 *     https://www.fda.gov/medical-devices/home-use-tests/pregnancy
 *   - [Progesterone label] US label for progesterone vaginal gel: breast
 *     tenderness, nausea, bloating and cramps.
 *   - [CDC fever] 38 °C / 100.4 °F or above.
 *     https://www.cdc.gov/port-health/php/definitions-symptoms-reportable-illness
 */

import type { CycleType } from "@src/packages/cycles/hooks/useCycles";
import { presentRetrievalPreparation } from "@src/packages/cycles/presenters/retrievalPreparation";
import type { StepPerspective } from "@src/packages/homepage/sections/treatmentFlows";
import type { TranslationFunction } from "@src/packages/i18n";

const NS = "step_explainers";

export const STEP_EXPLAINER_IDS = [
  "pre_treatment_stimulated",
  "pre_treatment_fet",
  "pre_treatment_iui_oi",
  "pre_treatment_sperm",
  "ovarian_stimulation_ivf",
  "ovarian_stimulation_iui",
  "ovulation_induction",
  "lining_preparation",
  "trigger_shot",
  "egg_retrieval",
  "sperm_collection",
  "fertilization",
  "embryo_development",
  "pgt",
  "freezing_eggs",
  "freezing_sperm",
  "semen_analysis",
  "screening",
  "embryo_transfer",
  "insemination",
  "luteal_support",
  "wait_after_transfer",
  "wait_after_insemination_or_ovulation",
] as const;

export type StepExplainerId = (typeof STEP_EXPLAINER_IDS)[number];

/** In display order. */
export const EXPLAINER_SECTION_IDS = [
  "what_happens",
  "before",
  "on_the_day",
  "after",
  "when_to_call",
] as const;

export type ExplainerSectionId = (typeof EXPLAINER_SECTION_IDS)[number];

/**
 * What kind of step it is, which only changes two section labels: a procedure
 * has "Before the day" and "On the day"; a phase, a lab step or a wait has
 * "Before you start" and "During this step".
 */
type ExplainerKind = "procedure" | "phase" | "lab" | "wait";

export type StepExplainerContext = {
  /** The hub step id (`TreatmentStepConfig.id`). */
  stepId: string;
  cycleType: CycleType;
  perspective: StepPerspective;
  /** Frozen eggs drive an IVF or ICSI cycle: lining preparation, then thaw. */
  usingFrozenOocytes: boolean;
  /** This step's protocol includes a trigger shot (`TreatmentStepConfig.hasTriggerShot`). */
  hasTriggerShot: boolean;
  /**
   * The cycle had, or plans, a trigger shot. The cycle records when it was
   * taken, not which drug it was, so the waits say "if it was hCG".
   */
  cycleHadTriggerShot: boolean;
};

export type StepExplainerBuildContext = StepExplainerContext & {
  /** See `findRetrievalClinicInstructions`. Only the egg retrieval reads it. */
  retrievalClinicInstructions?: string | null;
};

export type StepExplainerSection = {
  id: ExplainerSectionId;
  label: string;
  items: string[];
};

export type StepExplainer = {
  id: StepExplainerId;
  title: string;
  sections: StepExplainerSection[];
};

type Predicate = (context: StepExplainerContext) => boolean;

type CopyItem = {
  /** Leaf key, under this explainer and section unless `from` says otherwise. */
  key: string;
  /** Where the copy was written, when another explainer says the same thing. */
  from?: { explainer: StepExplainerId; section: ExplainerSectionId };
  when?: Predicate;
};

/**
 * The retrieval preparation lines from `presentRetrievalPreparation`: her
 * clinic's own instructions when they are on record, otherwise the line that
 * sends her to the clinic's timing, then what holds either way.
 */
type RetrievalPreparationItem = { retrievalPreparation: true };

type ItemSpec = CopyItem | RetrievalPreparationItem;

type StepExplainerSpec = {
  kind: ExplainerKind;
  /** Leaf key of the title, directly under `step_explainers.<id>`. */
  title: string;
  sections: Partial<Record<ExplainerSectionId, readonly ItemSpec[]>>;
};

const forSelf: Predicate = (c) => c.perspective === "self";
const forPartner: Predicate = (c) => c.perspective === "partner";
const isFet: Predicate = (c) => c.cycleType === "fet";
const isEggFreezing: Predicate = (c) => c.cycleType === "egg_freezing";
const isEmbryoFreezing: Predicate = (c) => c.cycleType === "embryo_freezing";
const usesFrozenOocytes: Predicate = (c) =>
  c.usingFrozenOocytes && (c.cycleType === "ivf" || c.cycleType === "icsi");
/** The lining was prepared with medication rather than by her own cycle. */
const liningWasPrepared: Predicate = (c) => isFet(c) || usesFrozenOocytes(c);
const followsTransfer: Predicate = (c) =>
  c.cycleType === "ivf" || c.cycleType === "icsi" || c.cycleType === "fet";
/** The person reading gives the sample: a man freezing sperm, or the partner. */
const givesTheSample: Predicate = (c) =>
  c.cycleType === "sperm_freezing" || c.perspective === "partner";
const forSpermFreezing: Predicate = (c) => c.cycleType === "sperm_freezing";

const not =
  (predicate: Predicate): Predicate =>
  (c) =>
    !predicate(c);
const all =
  (...predicates: Predicate[]): Predicate =>
  (c) =>
    predicates.every((predicate) => predicate(c));

const from = (
  explainer: StepExplainerId,
  section: ExplainerSectionId,
  key: string,
  when?: Predicate,
): CopyItem => ({ key, from: { explainer, section }, when });

const STEP_EXPLAINERS: Record<StepExplainerId, StepExplainerSpec> = {
  pre_treatment_stimulated: {
    kind: "phase",
    title: "how_getting_ready_for_stimulation_works",
    sections: {
      // Baseline bloods and scan on cycle day 2 to 4 [ASRM ovarian reserve
      // 2020]. The pill, estrogen or progesterone can schedule the cycle, and a
      // long agonist protocol is still an option [ESHRE OS 2025].
      what_happens: [{ key: "clinic_checks_you_are_ready_to_start" }],
      before: [
        { key: "fill_prescriptions_and_confirm_delivery" },
        { key: "go_to_your_injection_training" },
        { key: "set_up_a_space_to_store_medications" },
        // [USPSTF folic acid 2023]
        { key: "clinic_may_ask_you_to_start_a_prenatal_vitamin" },
      ],
      on_the_day: [
        { key: "baseline_tests_early_in_your_cycle" },
        { key: "review_your_medication_plan_with_your_nurse" },
        { key: "possible_medication_to_suppress_your_cycle_first" },
      ],
      // Practical reasons only: no guideline names a warning sign before
      // stimulation starts.
      when_to_call: [
        { key: "period_does_not_start_when_expected" },
        { key: "questions_about_your_medication_plan" },
        { key: "help_understanding_your_calendar" },
      ],
    },
  },

  pre_treatment_fet: {
    kind: "phase",
    title: "how_getting_ready_for_the_transfer_works",
    sections: {
      what_happens: [
        {
          key: "clinic_checks_you_are_ready_before_lining_preparation",
          when: forSelf,
        },
        { key: "clinic_checks_your_partner_is_ready", when: forPartner },
      ],
      before: [
        from(
          "pre_treatment_stimulated",
          "before",
          "fill_prescriptions_and_confirm_delivery",
          forSelf,
        ),
        from(
          "pre_treatment_stimulated",
          "before",
          "clinic_may_ask_you_to_start_a_prenatal_vitamin",
          forSelf,
        ),
      ],
      on_the_day: [
        from(
          "lining_preparation",
          "on_the_day",
          "timing_follows_your_medications_or_ovulation",
          forSelf,
        ),
        from(
          "lining_preparation",
          "on_the_day",
          "timing_follows_her_medications_or_ovulation",
          forPartner,
        ),
      ],
      when_to_call: [
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "period_does_not_start_when_expected",
          forSelf,
        ),
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "questions_about_your_medication_plan",
          forSelf,
        ),
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "help_understanding_your_calendar",
          forSelf,
        ),
        { key: "her_period_does_not_start_when_expected", when: forPartner },
        { key: "questions_about_her_medication_plan", when: forPartner },
      ],
    },
  },

  pre_treatment_iui_oi: {
    kind: "phase",
    title: "how_getting_ready_for_the_cycle_works",
    sections: {
      what_happens: [{ key: "baseline_check_sets_the_timing" }],
      before: [
        { key: "clinic_tells_you_when_to_start_any_medication" },
        from(
          "pre_treatment_stimulated",
          "before",
          "clinic_may_ask_you_to_start_a_prenatal_vitamin",
        ),
      ],
      when_to_call: [
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "period_does_not_start_when_expected",
        ),
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "questions_about_your_medication_plan",
        ),
        from(
          "pre_treatment_stimulated",
          "when_to_call",
          "help_understanding_your_calendar",
        ),
      ],
    },
  },

  pre_treatment_sperm: {
    kind: "phase",
    title: "how_getting_ready_for_the_collection_works",
    sections: {
      // Antioxidants have not been shown to help: [MOXI 2020], [AUA/ASRM male
      // 2020], [ESHRE add-ons 2023].
      what_happens: [{ key: "sperm_respond_to_changes_over_weeks" }],
      before: [
        from(
          "sperm_collection",
          "before",
          "abstinence_window_two_to_seven_days",
        ),
        from(
          "sperm_collection",
          "before",
          "heat_alcohol_and_drugs_lower_sperm_quality",
        ),
      ],
      when_to_call: [
        // [Sergerie 2007]
        from(
          "sperm_collection",
          "when_to_call",
          "fever_in_the_past_two_to_three_months",
        ),
        { key: "questions_about_supplements_or_medicines" },
      ],
    },
  },

  ovarian_stimulation_ivf: {
    kind: "phase",
    title: "how_ovarian_stimulation_works",
    sections: {
      // FSH alone or with LH, monitored by scan, with blood tests optional
      // [ESHRE OS 2025].
      what_happens: [
        { key: "injections_help_several_eggs_mature", when: forSelf },
        { key: "injections_help_several_of_her_eggs_mature", when: forPartner },
      ],
      before: [
        { key: "learn_the_injection_technique_from_your_nurse", when: forSelf },
        { key: "sit_in_on_the_injection_training", when: forPartner },
        { key: "store_the_medications_as_instructed" },
        { key: "take_injections_at_the_same_time_each_day", when: forSelf },
        { key: "help_her_stay_on_schedule", when: forPartner },
        { key: "clinic_tells_you_which_medicines_are_safe", when: forSelf },
      ],
      on_the_day: [
        { key: "mild_bloating_and_fullness", when: forSelf },
        { key: "she_may_feel_bloated_and_full", when: forPartner },
        { key: "tenderness_or_bruising_at_the_injection_sites" },
        { key: "possible_mood_changes_from_the_hormones" },
        { key: "clinic_tells_you_which_exercise_is_fine", when: forSelf },
        {
          key: "frequent_monitoring_visits_often_early_in_the_morning",
          when: forPartner,
        },
        // [Boivin 2011]
        { key: "stress_has_not_been_shown_to_decide_the_result" },
      ],
      // The trigger ends stimulation on the same tab, so it is explained here
      // from the trigger's own copy. Egg freezing has a separate trigger step.
      after: [
        from(
          "trigger_shot",
          "what_happens",
          "trigger_finishes_maturing_the_eggs",
          (c) => c.hasTriggerShot,
        ),
        from(
          "trigger_shot",
          "on_the_day",
          "usually_one_injection_at_the_exact_time",
          (c) => c.hasTriggerShot,
        ),
      ],
      // OHSS signs [ASRM OHSS 2024, ASRM OHSS facts 2023]; a twisted ovary
      // [Torsion]. A fever before the retrieval is checked first [ESHRE OPU
      // 2019], and 38 °C is the fever line [CDC fever].
      when_to_call: [
        { key: "severe_abdominal_pain_or_bloating" },
        { key: "weight_gain_breathlessness_swelling_nausea_or_less_urine" },
        { key: "sudden_severe_pain_on_one_side" },
        from(
          "egg_retrieval",
          "when_to_call",
          "heavy_bleeding_more_than_a_period",
        ),
        { key: "trouble_with_the_injections" },
        { key: "missed_or_mistimed_dose", when: forSelf },
        { key: "she_misses_a_dose", when: forPartner },
        from(
          "trigger_shot",
          "when_to_call",
          "missed_or_late_trigger",
          all(forSelf, (c) => c.hasTriggerShot),
        ),
        {
          key: "she_misses_the_trigger_or_takes_it_late",
          when: all(forPartner, (c) => c.hasTriggerShot),
        },
        from("egg_retrieval", "when_to_call", "fever_over_38"),
      ],
    },
  },

  ovarian_stimulation_iui: {
    kind: "phase",
    title: "how_stimulation_for_iui_works",
    sections: {
      // Tablets or low-dose injections for one or two follicles [ASRM
      // unexplained 2020, ESHRE unexplained 2023]. A trigger is one way to time
      // the insemination, not the only one [IUI timing 2025], so it is "often".
      what_happens: [
        { key: "medication_helps_one_or_two_follicles_grow", when: forSelf },
        {
          key: "medication_helps_one_or_two_of_her_follicles_grow",
          when: forPartner,
        },
      ],
      before: [
        { key: "take_medication_on_the_days_your_clinic_gives", when: forSelf },
        { key: "ask_your_nurse_to_show_you_the_injections", when: forSelf },
        { key: "ask_when_to_take_the_trigger_and_come_in", when: forSelf },
        {
          key: "sit_in_on_the_injection_training_if_there_is_one",
          when: forPartner,
        },
        { key: "help_her_stay_on_schedule_with_each_dose", when: forPartner },
        {
          key: "keep_insemination_day_free_if_giving_the_sample",
          when: forPartner,
        },
      ],
      on_the_day: [
        { key: "tablets_or_low_dose_injections" },
        { key: "fewer_scans_than_in_ivf" },
        { key: "mild_bloating_or_pelvic_twinges" },
        { key: "trigger_once_a_follicle_is_ready" },
      ],
      when_to_call: [
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "missed_or_mistimed_dose",
          forSelf,
        ),
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "she_misses_a_dose",
          forPartner,
        ),
        // OHSS after gonadotropins [ESHRE unexplained 2023, ASRM gonadotropin
        // OI 2020]; a twisted ovary [Torsion].
        { key: "severe_pain_a_swollen_belly_or_heavy_bleeding" },
        { key: "unsure_about_the_trigger_or_insemination_timing" },
      ],
    },
  },

  ovulation_induction: {
    kind: "phase",
    title: "how_ovulation_induction_works",
    sections: {
      // Tablets first, then injections with scan monitoring [PCOS 2023].
      what_happens: [
        { key: "medication_supports_follicle_growth_and_ovulation" },
      ],
      before: [
        from(
          "ovarian_stimulation_iui",
          "before",
          "take_medication_on_the_days_your_clinic_gives",
        ),
        { key: "clinic_tells_you_when_to_test_or_start_support" },
      ],
      on_the_day: [
        { key: "oral_or_injectable_medication" },
        { key: "monitoring_if_your_clinic_orders_it" },
        { key: "trigger_shot_or_timed_intercourse" },
      ],
      when_to_call: [
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "missed_or_mistimed_dose",
        ),
        // A swollen belly can be OHSS, which ovulation induction can cause
        // [ASRM gonadotropin OI 2020, ASRM OHSS facts 2023].
        from(
          "ovarian_stimulation_iui",
          "when_to_call",
          "severe_pain_a_swollen_belly_or_heavy_bleeding",
        ),
        { key: "unsure_about_trigger_or_timing_instructions" },
      ],
    },
  },

  lining_preparation: {
    kind: "phase",
    title: "how_lining_preparation_works",
    sections: {
      // About two weeks of estrogen; the progesterone start sets the transfer
      // day [Mumusoglu 2021]. No thickness cut-off fits everyone [Kasius 2014].
      what_happens: [
        { key: "estrogen_thickens_the_lining", when: forSelf },
        { key: "estrogen_thickens_her_lining", when: forPartner },
        {
          key: "no_stimulation_needed_with_frozen_eggs",
          when: usesFrozenOocytes,
        },
      ],
      on_the_day: [
        { key: "no_single_thickness_applies_to_everyone", when: forSelf },
        {
          key: "no_single_thickness_applies_to_everyone_for_her",
          when: forPartner,
        },
        { key: "progesterone_start_sets_your_transfer_date", when: forSelf },
        { key: "progesterone_start_sets_the_transfer_date", when: forPartner },
        { key: "timing_follows_your_medications_or_ovulation", when: forSelf },
        {
          key: "timing_follows_her_medications_or_ovulation",
          when: forPartner,
        },
      ],
      when_to_call: [
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "missed_or_mistimed_dose",
          forSelf,
        ),
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "she_misses_a_dose",
          forPartner,
        ),
        { key: "heavy_bleeding_before_the_transfer" },
        { key: "unsure_when_to_start_progesterone" },
      ],
    },
  },

  trigger_shot: {
    kind: "procedure",
    title: "how_the_trigger_shot_works",
    sections: {
      // About 36 hours to the retrieval [ESHRE OPU 2019]; some protocols use a
      // dual trigger [ESHRE OS 2025].
      what_happens: [{ key: "trigger_finishes_maturing_the_eggs" }],
      before: [
        { key: "set_an_alarm_for_the_exact_time" },
        { key: "after_hours_number_if_something_goes_wrong" },
        { key: "confirm_your_retrieval_time" },
      ],
      on_the_day: [
        { key: "usually_one_injection_at_the_exact_time" },
        { key: "cramping_or_bloating_may_increase" },
        { key: "you_may_feel_emotionally_heightened" },
      ],
      when_to_call: [
        { key: "unsure_how_to_mix_or_give_the_trigger" },
        { key: "missed_or_late_trigger" },
        { key: "reaction_to_the_injection" },
      ],
    },
  },

  egg_retrieval: {
    kind: "procedure",
    title: "how_egg_retrieval_works",
    sections: {
      // About 36 hours after the trigger [ESHRE OPU 2019].
      what_happens: [
        { key: "needle_collects_the_eggs_under_sedation", when: forSelf },
        {
          key: "needle_collects_your_partners_eggs_under_sedation",
          when: forPartner,
        },
      ],
      before: [{ retrievalPreparation: true }],
      on_the_day: [
        {
          key: "asleep_or_very_drowsy_and_feel_little_or_no_pain",
          when: forSelf,
        },
        { key: "she_is_asleep_or_very_drowsy", when: forPartner },
        { key: "not_every_follicle_holds_an_egg" },
        { key: "clinic_tells_you_how_many_eggs_were_collected" },
        { key: "you_rest_until_the_sedation_wears_off", when: forSelf },
        { key: "she_rests_until_the_sedation_wears_off", when: forPartner },
      ],
      after: [
        {
          key: "cramping_bloating_and_light_spotting_are_common",
          when: forSelf,
        },
        {
          key: "she_may_have_cramping_bloating_and_spotting",
          when: forPartner,
        },
        {
          key: "rest_fluids_and_your_clinics_pain_relief_advice",
          when: forSelf,
        },
        { key: "give_her_a_quiet_day_to_rest", when: forPartner },
        {
          key: "lab_fertilizes_the_mature_eggs_the_same_day",
          when: not(isEggFreezing),
        },
        {
          key: "lab_checks_which_eggs_are_mature_and_freezes_them",
          when: isEggFreezing,
        },
      ],
      // Bleeding, inside too (dizziness or fainting), and infection [ESHRE OPU
      // 2019, Retrieval bleeding]; OHSS [ASRM OHSS 2024, ASRM OHSS facts 2023];
      // a clot in a leg or lung [ASRM OHSS facts 2023, RCOG OHSS 2016].
      when_to_call: [
        { key: "heavy_bleeding_more_than_a_period" },
        { key: "feeling_dizzy_or_faint" },
        { key: "fever_over_38" },
        { key: "severe_pain_that_pain_relief_does_not_ease" },
        { key: "trouble_urinating_or_much_less_urine" },
        { key: "nausea_rapid_weight_gain_or_a_badly_swollen_belly" },
        { key: "trouble_breathing_chest_pain_or_a_swollen_leg" },
      ],
    },
  },

  sperm_collection: {
    kind: "procedure",
    title: "how_sperm_collection_works",
    sections: {
      what_happens: [
        {
          key: "sample_prepared_for_use_that_day",
          when: not(forSpermFreezing),
        },
        { key: "sample_given_and_frozen_for_later", when: forSpermFreezing },
      ],
      // Abstinence, collection and a home sample [WHO semen 2021]; saliva,
      // heat, alcohol and drugs [ASRM natural fertility 2022, Heat and
      // alcohol]; fever [Sergerie 2007].
      before: [
        { key: "abstinence_window_two_to_seven_days", when: givesTheSample },
        {
          key: "still_go_if_the_gap_is_shorter_or_longer",
          when: givesTheSample,
        },
        {
          key: "heat_alcohol_and_drugs_lower_sperm_quality",
          when: givesTheSample,
        },
        {
          key: "fresh_sample_after_two_to_seven_days",
          when: not(givesTheSample),
        },
        {
          key: "bring_id_and_donor_or_partner_paperwork",
          when: not(givesTheSample),
        },
      ],
      on_the_day: [
        {
          key: "collect_the_whole_sample_into_the_sterile_cup",
          when: givesTheSample,
        },
        { key: "avoid_lubricant_saliva_and_condoms", when: givesTheSample },
        { key: "private_room_and_photo_id", when: givesTheSample },
        { key: "at_home_only_if_your_clinic_allows_it", when: givesTheSample },
        {
          key: "sample_collected_at_the_clinic_or_brought_from_home",
          when: not(givesTheSample),
        },
      ],
      after: [
        {
          key: "lab_washes_and_prepares_the_sample",
          when: not(forSpermFreezing),
        },
        from(
          "semen_analysis",
          "on_the_day",
          "analysis_comes_back_within_two_to_five_days",
          forSpermFreezing,
        ),
      ],
      when_to_call: [
        { key: "collection_may_be_delayed_or_difficult" },
        { key: "fever_in_the_past_two_to_three_months" },
        { key: "cannot_produce_a_sample_on_the_day", when: givesTheSample },
      ],
    },
  },

  // Day 1: which eggs fertilized. Its own tab, so it explains only that day;
  // the days after are the embryo development explainer's.
  fertilization: {
    kind: "lab",
    title: "how_fertilization_works",
    sections: {
      what_happens: [
        {
          key: "lab_puts_each_egg_with_sperm",
          when: not(usesFrozenOocytes),
        },
        {
          key: "lab_thaws_the_eggs_and_fertilizes_them",
          when: usesFrozenOocytes,
        },
      ],
      // Fertilization rates [Vienna 2017]; warmed eggs and ICSI [ASRM oocyte
      // cryo 2013].
      on_the_day: [
        { key: "ivf_or_icsi", when: not(usesFrozenOocytes) },
        { key: "not_every_egg_survives_thawing", when: usesFrozenOocytes },
        { key: "some_mature_eggs_do_not_fertilize" },
      ],
      // These lab days fall in the week after the trigger, and early OHSS
      // starts 4 to 7 days after it [ASRM OHSS 2024], so the signs to watch
      // after a retrieval are here too. Frozen eggs had no retrieval this cycle.
      when_to_call: [
        { key: "numbers_you_were_told_do_not_match_your_report" },
        { key: "help_reading_the_lab_report" },
        from(
          "freezing_eggs",
          "when_to_call",
          "warning_signs_after_the_retrieval",
          not(usesFrozenOocytes),
        ),
      ],
    },
  },

  // Days 3 to 6: cell stages, the blastocyst, and what happens to it.
  embryo_development: {
    kind: "lab",
    title: "how_embryos_develop_in_the_lab",
    sections: {
      // Day 3 cells and day 5 to 7 blastocysts [Istanbul 2025]; one embryo
      // transferred [ASRM embryos 2021].
      what_happens: [{ key: "lab_watches_the_embryos_until_day_5_or_6" }],
      on_the_day: [
        { key: "some_embryos_stop_growing" },
        { key: "clinic_tells_you_when_to_expect_news" },
      ],
      after: [
        {
          key: "blastocysts_can_be_transferred_frozen_or_tested",
          when: not(isEmbryoFreezing),
        },
        {
          key: "usually_one_embryo_transferred_others_frozen",
          when: not(isEmbryoFreezing),
        },
        { key: "blastocysts_are_frozen_and_stored", when: isEmbryoFreezing },
        { key: "grades_help_choose_which_to_transfer_first" },
      ],
      when_to_call: [
        from(
          "fertilization",
          "when_to_call",
          "numbers_you_were_told_do_not_match_your_report",
        ),
        from("fertilization", "when_to_call", "help_reading_the_lab_report"),
        // Still the early OHSS window: see fertilization.
        from(
          "freezing_eggs",
          "when_to_call",
          "warning_signs_after_the_retrieval",
          not(usesFrozenOocytes),
        ),
      ],
    },
  },

  pgt: {
    kind: "lab",
    title: "how_genetic_testing_of_embryos_works",
    sections: {
      what_happens: [{ key: "cells_are_taken_and_sent_to_a_genetics_lab" }],
      // Euploid, aneuploid, mosaic or no result [ASRM mosaic 2023, ASRM PGT-A
      // 2024]; nothing here says PGT-A improves the chances, which it has not
      // been shown to do for everyone [ESHRE add-ons 2023].
      on_the_day: [
        {
          key: "each_embryo_is_reported_euploid_aneuploid_mosaic_or_no_result",
        },
        { key: "results_show_which_embryos_your_clinic_may_transfer_first" },
      ],
      when_to_call: [
        from(
          "fertilization",
          "when_to_call",
          "numbers_you_were_told_do_not_match_your_report",
        ),
        from("fertilization", "when_to_call", "help_reading_the_lab_report"),
      ],
    },
  },

  freezing_eggs: {
    kind: "lab",
    title: "how_your_eggs_are_frozen",
    sections: {
      what_happens: [
        { key: "lab_checks_which_eggs_are_mature_enough_to_freeze" },
      ],
      on_the_day: [
        { key: "how_many_eggs_were_retrieved_and_mature" },
        { key: "how_many_eggs_were_frozen" },
        { key: "where_your_eggs_are_stored" },
      ],
      when_to_call: [
        from(
          "fertilization",
          "when_to_call",
          "numbers_you_were_told_do_not_match_your_report",
        ),
        { key: "missing_the_lab_or_storage_report" },
        // The egg retrieval's warning signs in one line, for the days after
        // it: bleeding, infection, OHSS [ESHRE OPU 2019, Retrieval bleeding,
        // ASRM OHSS 2024].
        { key: "warning_signs_after_the_retrieval" },
      ],
    },
  },

  freezing_sperm: {
    kind: "lab",
    title: "how_your_sample_is_frozen",
    sections: {
      // Freezing costs some motility [WHO semen 2021].
      what_happens: [{ key: "lab_divides_and_freezes_the_sample" }],
      on_the_day: [
        from("semen_analysis", "on_the_day", "figures_decide_usable_vials"),
        { key: "some_sperm_do_not_survive_thawing" },
      ],
      after: [{ key: "vial_count_freeze_date_and_storage" }],
      when_to_call: [
        { key: "vial_count_does_not_match_your_report" },
        { key: "missing_storage_location_or_specimen_ids" },
        { key: "need_to_move_storage_to_another_facility" },
      ],
    },
  },

  semen_analysis: {
    kind: "lab",
    title: "how_the_semen_analysis_works",
    sections: {
      // The measures [WHO semen 2021].
      what_happens: [{ key: "lab_measures_the_sample" }],
      on_the_day: [
        { key: "figures_decide_usable_vials" },
        { key: "analysis_comes_back_within_two_to_five_days" },
        { key: "report_with_notes_on_sample_quality" },
      ],
      when_to_call: [
        { key: "result_missing_or_hard_to_interpret" },
        { key: "sample_could_not_be_frozen" },
        { key: "clinic_recommends_repeat_testing" },
      ],
    },
  },

  screening: {
    kind: "lab",
    title: "how_screening_works",
    sections: {
      // Viral screening before storage [ASRM viral 2023, WHO semen 2021].
      what_happens: [{ key: "clinics_require_screening_before_storage" }],
      on_the_day: [
        { key: "infectious_disease_panel_results" },
        { key: "possible_genetic_screening_results" },
        { key: "documents_for_the_storage_lab" },
      ],
      when_to_call: [
        { key: "required_screening_report_missing" },
        { key: "result_needs_clarification" },
        { key: "clinic_will_not_release_the_sample" },
      ],
    },
  },

  embryo_transfer: {
    kind: "procedure",
    title: "how_embryo_transfer_works",
    sections: {
      // Ultrasound guidance, and no benefit from lying down after [ASRM
      // transfer 2017]; most blastocysts survive warming [Vienna 2017].
      what_happens: [
        {
          key: "embryo_placed_through_a_soft_tube",
          when: all(forSelf, not(isFet)),
        },
        { key: "embryo_warmed_and_placed", when: all(forSelf, isFet) },
        {
          key: "embryo_placed_in_your_partners_uterus",
          when: all(forPartner, not(isFet)),
        },
        {
          key: "embryo_warmed_and_placed_in_your_partners_uterus",
          when: all(forPartner, isFet),
        },
      ],
      before: [
        { key: "some_clinics_ask_for_a_full_bladder" },
        { key: "bring_photo_id_clinic_checks_the_embryo" },
      ],
      on_the_day: [
        { key: "most_frozen_embryos_survive_warming", when: isFet },
        { key: "you_can_go_about_your_day", when: forSelf },
        { key: "she_can_go_about_her_day", when: forPartner },
      ],
      // Support until at least the test [ESHRE OS 2025]; with no corpus
      // luteum it carries the pregnancy for weeks [Mumusoglu 2021]. The test
      // date [Hughes 2022].
      after: [
        { key: "keep_taking_medications_until_told_to_stop", when: forSelf },
        {
          key: "she_keeps_taking_medications_until_told_to_stop",
          when: forPartner,
        },
        {
          key: "stopping_early_can_put_a_pregnancy_at_risk",
          when: all(forSelf, liningWasPrepared),
        },
        {
          key: "stopping_early_can_put_her_pregnancy_at_risk",
          when: all(forPartner, liningWasPrepared),
        },
        {
          key: "pregnancy_blood_test_nine_to_fourteen_days_after",
          when: forSelf,
        },
        { key: "clinic_gives_the_date_of_her_blood_test", when: forPartner },
        { key: "cramping_and_spotting_do_not_tell_you_the_result" },
      ],
      when_to_call: [
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "missed_or_mistimed_dose",
          forSelf,
        ),
        from(
          "ovarian_stimulation_ivf",
          "when_to_call",
          "she_misses_a_dose",
          forPartner,
        ),
        { key: "heavy_bleeding_severe_pain_or_fever" },
        // A fresh transfer falls in the early OHSS window, and a pregnancy
        // can bring late OHSS [ASRM OHSS 2024]. A frozen or prepared cycle
        // had no stimulation.
        from(
          "wait_after_transfer",
          "when_to_call",
          "shortness_of_breath_or_rapid_severe_bloating",
          not(liningWasPrepared),
        ),
      ],
    },
  },

  insemination: {
    kind: "procedure",
    title: "how_insemination_works",
    sections: {
      // Timed by the trigger or ovulation [ASRM unexplained 2020]; luteal
      // support helps after injections only [Green 2017].
      what_happens: [
        { key: "prepared_sample_placed_in_your_uterus", when: forSelf },
        {
          key: "prepared_sample_placed_in_your_partners_uterus",
          when: forPartner,
        },
      ],
      before: [
        { key: "take_the_trigger_at_the_time_your_clinic_gave", when: forSelf },
        { key: "extra_ovulation_tests_are_usually_unnecessary", when: forSelf },
        from(
          "sperm_collection",
          "before",
          "bring_id_and_donor_or_partner_paperwork",
        ),
      ],
      on_the_day: [
        { key: "timing_is_set_around_ovulation_or_the_trigger" },
        { key: "mild_cramping_or_spotting_afterwards" },
      ],
      after: [{ key: "clinic_tells_you_when_to_test_and_start_support" }],
      when_to_call: [
        from(
          "embryo_transfer",
          "when_to_call",
          "heavy_bleeding_severe_pain_or_fever",
        ),
        { key: "unsure_about_the_timing_after_the_trigger" },
        { key: "need_to_reschedule_the_appointment" },
      ],
    },
  },

  luteal_support: {
    kind: "phase",
    title: "how_support_medication_works",
    sections: {
      // [ESHRE OS 2025, Mumusoglu 2021]; progesterone side effects [Progesterone
      // label].
      what_happens: [
        {
          key: "progesterone_supports_the_lining_after_the_transfer",
          when: all(forSelf, followsTransfer),
        },
        {
          key: "progesterone_supports_her_lining_after_the_transfer",
          when: all(forPartner, followsTransfer),
        },
        {
          key: "support_medication_helps_the_lining_while_you_wait",
          when: all(forSelf, not(followsTransfer)),
        },
        {
          key: "support_medication_helps_her_lining_while_you_wait",
          when: all(forPartner, not(followsTransfer)),
        },
      ],
      on_the_day: [
        { key: "keep_taking_it_until_your_clinic_says_stop", when: forSelf },
        {
          key: "she_keeps_taking_it_until_the_clinic_says_stop",
          when: forPartner,
        },
        from(
          "embryo_transfer",
          "after",
          "stopping_early_can_put_a_pregnancy_at_risk",
          all(forSelf, liningWasPrepared),
        ),
        from(
          "embryo_transfer",
          "after",
          "stopping_early_can_put_her_pregnancy_at_risk",
          all(forPartner, liningWasPrepared),
        ),
        { key: "progesterone_can_mimic_early_pregnancy_signs" },
        { key: "ask_before_stopping_if_bleeding_starts", when: forSelf },
        { key: "ask_before_she_stops_if_bleeding_starts", when: forPartner },
      ],
      when_to_call: [
        { key: "missed_dose_or_running_low", when: forSelf },
        { key: "she_misses_a_dose_or_is_running_low", when: forPartner },
        from(
          "embryo_transfer",
          "when_to_call",
          "heavy_bleeding_severe_pain_or_fever",
        ),
        // Support runs through the days when a pregnancy can bring late OHSS
        // after stimulation [ASRM OHSS 2024, ASRM gonadotropin OI 2020].
        from(
          "wait_after_transfer",
          "when_to_call",
          "shortness_of_breath_or_rapid_severe_bloating",
          not(liningWasPrepared),
        ),
      ],
    },
  },

  wait_after_transfer: {
    kind: "wait",
    title: "how_the_wait_for_the_blood_test_works",
    sections: {
      // The test date and the 48-hour repeat [Hughes 2022]; a trigger on a
      // home test [hCG clearance]; no bed rest [ASRM transfer 2017].
      what_happens: [
        { key: "blood_test_shows_whether_an_embryo_implanted", when: forSelf },
        from(
          "embryo_transfer",
          "after",
          "pregnancy_blood_test_nine_to_fourteen_days_after",
          forSelf,
        ),
        {
          key: "her_blood_test_shows_whether_an_embryo_implanted",
          when: forPartner,
        },
        from(
          "embryo_transfer",
          "after",
          "clinic_gives_the_date_of_her_blood_test",
          forPartner,
        ),
      ],
      on_the_day: [
        { key: "symptoms_are_not_a_result" },
        { key: "home_tests_can_mislead_either_way" },
        {
          key: "if_your_trigger_was_hcg_an_early_positive_can_be_leftover",
          when: all(forSelf, (c) => c.cycleHadTriggerShot),
        },
        {
          key: "if_her_trigger_was_hcg_an_early_positive_can_be_leftover",
          when: all(forPartner, (c) => c.cycleHadTriggerShot),
        },
        { key: "a_negative_now_is_not_reliable" },
        { key: "rest_fluids_and_gentle_activity_are_fine" },
      ],
      after: [
        { key: "clinics_usually_repeat_a_positive_test" },
        {
          key: "keep_taking_your_medications_until_your_clinic_changes_them",
          when: forSelf,
        },
        {
          key: "she_keeps_taking_her_medications_until_the_clinic_changes_them",
          when: forPartner,
        },
        { key: "the_clinic_blood_test_decides_the_next_step" },
      ],
      // Ectopic pregnancy [Ectopic]; late OHSS [ASRM OHSS 2024]; support
      // [ESHRE psychosocial 2015].
      when_to_call: [
        { key: "severe_or_one_sided_pelvic_pain_or_shoulder_tip_pain" },
        { key: "heavy_bleeding_fever_or_feeling_dizzy_or_faint" },
        { key: "shortness_of_breath_or_rapid_severe_bloating" },
        { key: "emotional_distress_support_is_available" },
      ],
    },
  },

  wait_after_insemination_or_ovulation: {
    kind: "wait",
    title: "how_the_wait_for_the_test_works",
    sections: {
      // Testing too early misleads [FDA pregnancy tests]; a trigger on a urine
      // test [hCG clearance].
      what_happens: [
        { key: "wait_for_the_test_date_your_clinic_set", when: forSelf },
        { key: "wait_together_for_the_test_date", when: forPartner },
        { key: "test_date_is_usually_about_two_weeks_later" },
      ],
      on_the_day: [
        {
          key: "keep_taking_support_medication_whatever_a_home_test_shows",
          when: forSelf,
        },
        {
          key: "she_keeps_taking_support_medication_whatever_a_home_test_shows",
          when: forPartner,
        },
        from(
          "luteal_support",
          "on_the_day",
          "ask_before_stopping_if_bleeding_starts",
          forSelf,
        ),
        from(
          "luteal_support",
          "on_the_day",
          "ask_before_she_stops_if_bleeding_starts",
          forPartner,
        ),
        { key: "cramping_or_spotting_does_not_tell_the_outcome" },
        {
          key: "hcg_trigger_can_make_early_tests_look_positive",
          when: (c) => c.cycleHadTriggerShot,
        },
        { key: "this_stretch_is_hard_for_most_people" },
      ],
      after: [
        { key: "your_clinics_test_date_is_the_one_for_decisions" },
        from(
          "wait_after_transfer",
          "after",
          "clinics_usually_repeat_a_positive_test",
        ),
        {
          key: "after_a_negative_your_clinic_says_when_to_stop_medication",
          when: forSelf,
        },
        {
          key: "after_a_negative_the_clinic_says_when_she_can_stop_medication",
          when: forPartner,
        },
        { key: "an_unclear_result_is_usually_repeated" },
        { key: "bleeding_does_not_need_to_be_interpreted_alone" },
      ],
      // The same warning signs as the wait after a transfer: an ectopic
      // pregnancy is possible after an insemination or ovulation induction too
      // [Ectopic], and so is OHSS after gonadotropins [ASRM gonadotropin OI
      // 2020].
      when_to_call: [
        from(
          "wait_after_transfer",
          "when_to_call",
          "severe_or_one_sided_pelvic_pain_or_shoulder_tip_pain",
        ),
        from(
          "wait_after_transfer",
          "when_to_call",
          "heavy_bleeding_fever_or_feeling_dizzy_or_faint",
        ),
        from(
          "wait_after_transfer",
          "when_to_call",
          "shortness_of_breath_or_rapid_severe_bloating",
        ),
        { key: "positive_home_test_before_the_scheduled_test" },
        from(
          "luteal_support",
          "when_to_call",
          "missed_dose_or_running_low",
          forSelf,
        ),
        from(
          "luteal_support",
          "when_to_call",
          "she_misses_a_dose_or_is_running_low",
          forPartner,
        ),
        { key: "help_deciding_what_to_do_with_a_result" },
      ],
    },
  },
};

/**
 * The explainer for the step she is looking at, or null for an id no flow
 * produces. Every step `getStepsForCycle` can return resolves to one, for
 * herself and for a partner; a test holds that.
 */
export function resolveStepExplainerId(
  context: StepExplainerContext,
): StepExplainerId | null {
  const { stepId, cycleType, perspective } = context;
  const frozenOocytes = usesFrozenOocytes(context);
  switch (stepId) {
    case "pre_treatment":
      if (cycleType === "fet") return "pre_treatment_fet";
      // A partner's own preparation is his sample; a man freezing sperm is
      // the patient, and the stimulation workup is not his.
      if (perspective === "partner" || cycleType === "sperm_freezing") {
        return "pre_treatment_sperm";
      }
      if (frozenOocytes) return "pre_treatment_fet";
      if (cycleType === "iui" || cycleType === "oi") {
        return "pre_treatment_iui_oi";
      }
      return "pre_treatment_stimulated";
    case "protocol":
    case "stimulation":
      if (cycleType === "fet" || frozenOocytes) return "lining_preparation";
      if (cycleType === "iui") return "ovarian_stimulation_iui";
      if (cycleType === "oi") return "ovulation_induction";
      return "ovarian_stimulation_ivf";
    case "trigger_shot":
      return "trigger_shot";
    case "egg_retrieval":
      return "egg_retrieval";
    case "sperm_collection":
      return "sperm_collection";
    case "fertilization":
      return "fertilization";
    case "embryo_development":
      return "embryo_development";
    case "pgt_testing":
      return "pgt";
    case "freezing":
      if (cycleType === "sperm_freezing") return "freezing_sperm";
      if (cycleType === "egg_freezing") return "freezing_eggs";
      // Embryo freezing has no freezing step of its own; its embryos are
      // frozen at the end of development, which that explainer covers.
      return "embryo_development";
    case "screening":
      return "screening";
    case "analysis":
      return "semen_analysis";
    case "transfer":
      return "embryo_transfer";
    case "insemination":
      return "insemination";
    case "luteal_phase":
      return "luteal_support";
    case "outcome":
      return cycleType === "iui" || cycleType === "oi"
        ? "wait_after_insemination_or_ovulation"
        : "wait_after_transfer";
    default:
      return null;
  }
}

function sectionLabelKey(
  section: ExplainerSectionId,
  kind: ExplainerKind,
): string {
  switch (section) {
    case "what_happens":
      return `${NS}.sections.what_happens`;
    case "before":
      return kind === "procedure"
        ? `${NS}.sections.before_the_day`
        : `${NS}.sections.before_you_start`;
    case "on_the_day":
      return kind === "procedure"
        ? `${NS}.sections.on_the_day`
        : `${NS}.sections.during_this_step`;
    case "after":
      return `${NS}.sections.afterwards`;
    case "when_to_call":
      return `${NS}.sections.when_to_call_your_clinic`;
  }
}

function copyKeyPath(
  id: StepExplainerId,
  section: ExplainerSectionId,
  item: CopyItem,
): string {
  const explainer = item.from?.explainer ?? id;
  const sourceSection = item.from?.section ?? section;
  return `${NS}.${explainer}.${sourceSection}.${item.key}`;
}

function renderItem(
  t: TranslationFunction,
  id: StepExplainerId,
  section: ExplainerSectionId,
  item: ItemSpec,
  context: StepExplainerBuildContext,
): string[] {
  if ("retrievalPreparation" in item) {
    const preparation = presentRetrievalPreparation({
      t,
      clinicInstructions: context.retrievalClinicInstructions ?? null,
      perspective: context.perspective,
    });
    return [preparation.fasting, ...preparation.practical];
  }
  if (item.when && !item.when(context)) return [];
  const key = copyKeyPath(id, section, item);
  const text = t(key);
  // `t` hands back the key path for a missing entry. Nothing is better than a
  // key path on screen; the catalogue test keeps this branch unreachable.
  return text === key ? [] : [text];
}

export function buildStepExplainer(
  t: TranslationFunction,
  id: StepExplainerId,
  context: StepExplainerBuildContext,
): StepExplainer {
  const spec = STEP_EXPLAINERS[id];
  const sections = EXPLAINER_SECTION_IDS.flatMap((sectionId) => {
    const items = (spec.sections[sectionId] ?? []).flatMap((item) =>
      renderItem(t, id, sectionId, item, context),
    );
    return items.length > 0
      ? [
          {
            id: sectionId,
            label: t(sectionLabelKey(sectionId, spec.kind)),
            items,
          },
        ]
      : [];
  });
  return { id, title: t(`${NS}.${id}.${spec.title}`), sections };
}

/**
 * Every catalogue key the explainers can read, for the catalogue test: a key
 * that exists in neither language, or in only one, fails there rather than
 * disappearing from a panel.
 */
export function listStepExplainerCopyKeys(): string[] {
  const keys = new Set<string>();
  for (const id of STEP_EXPLAINER_IDS) {
    const spec = STEP_EXPLAINERS[id];
    keys.add(`${NS}.${id}.${spec.title}`);
    for (const sectionId of EXPLAINER_SECTION_IDS) {
      keys.add(sectionLabelKey(sectionId, spec.kind));
      for (const item of spec.sections[sectionId] ?? []) {
        if ("retrievalPreparation" in item) continue;
        keys.add(copyKeyPath(id, sectionId, item));
      }
    }
  }
  return [...keys].sort();
}
